<template>
    <div class="alert-message" :class="`alert-message--${finalVariant}`">
        <div class="alert-message_icon">
            <sw-icon :name="getIcon" />
        </div>
        <div class="alert-message_content">
            <div v-if="title" class="alert-message_title">{{ title }}</div>
            <div class="alert-message_description" v-html="description"></div>
            <div v-if="$slots.default" class="alert-message_slot">
                <slot></slot>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { ALERT_VARIANTS } from '@/constants';

const props = defineProps({
    variant: {
        type: String,
        default: ALERT_VARIANTS.INFO,
        validator: (value) => Object.values(ALERT_VARIANTS).includes(value)
    },
    type: {
        type: String,
        default: '',
        validator: (value) => !value || Object.values(ALERT_VARIANTS).includes(value)
    },
    icon: {
        type: String,
        default: ''
    },
    title: {
        type: String,
        default: ''
    },
    description: {
        type: String,
        required: true
    }
});

// Computed para o variant final (type tem prioridade sobre variant)
const finalVariant = computed(() => {
    return props.type || props.variant;
});

const getIcon = computed(() => {
    // Se um ícone personalizado foi fornecido, usa ele
    if (props.icon) {
        return props.icon;
    }

    // Caso contrário, usa o ícone padrão baseado no variant/type
    const iconMap = {
        [ALERT_VARIANTS.INFO]: 'info',
        [ALERT_VARIANTS.SUCCESS]: 'checkmark',
        [ALERT_VARIANTS.WARNING]: 'notification',
        [ALERT_VARIANTS.ERROR]: 'cross'
    };
    return iconMap[finalVariant.value] || 'info';
});
</script>

<style lang="scss" scoped>
.alert-message {
    display: flex;
    align-items: flex-start;
    gap: 0.875em;
    padding: 1em 1.25em;
    border-radius: 0.75em;
    border: 1px solid;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8));
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 4px;
        border-radius: 0.75em 0 0 0.75em;
    }

    &--info {
        border-color: rgba(59, 130, 246, 0.25);
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(147, 197, 253, 0.05));

        &::before {
            background: linear-gradient(180deg, #3b82f6, #1d4ed8);
        }

        .alert-message_icon {
            color: #3b82f6;
            background: rgba(59, 130, 246, 0.12);
        }

        .alert-message_title {
            color: #1e40af;
        }
    }

    &--success {
        border-color: rgba(16, 185, 129, 0.25);
        background: linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(110, 231, 183, 0.05));

        &::before {
            background: linear-gradient(180deg, #10b981, #059669);
        }

        .alert-message_icon {
            color: #10b981;
            background: rgba(16, 185, 129, 0.12);
        }

        .alert-message_title {
            color: #047857;
        }
    }

    &--warning {
        border-color: rgba(245, 158, 11, 0.25);
        background: linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(251, 191, 36, 0.05));

        &::before {
            background: linear-gradient(180deg, #f59e0b, #d97706);
        }

        .alert-message_icon {
            color: #f59e0b;
            background: rgba(245, 158, 11, 0.12);
        }

        .alert-message_title {
            color: #92400e;
        }
    }

    &--error {
        border-color: rgba(239, 68, 68, 0.25);
        background: linear-gradient(135deg, rgba(239, 68, 68, 0.08), rgba(252, 165, 165, 0.05));

        &::before {
            background: linear-gradient(180deg, #ef4444, #dc2626);
        }

        .alert-message_icon {
            color: #ef4444;
            background: rgba(239, 68, 68, 0.12);
        }

        .alert-message_title {
            color: #991b1b;
        }
    }
}

.alert-message_icon {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 2.25em;
    height: 2.25em;
    border-radius: 0.5em;
    font-size: 1em;
    flex-shrink: 0;
    margin-top: 0.125em;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.alert-message_content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.375em;
    min-width: 0;
}

.alert-message_title {
    font-size: 0.875em;
    font-weight: 600;
    line-height: 1.3;
    margin: 0;
}

.alert-message_description {
    font-size: 0.875em;
    font-weight: 400;
    line-height: 1.5;
    color: var(--textSecondary, #64748b);
    margin: 0;
}

.alert-message_slot {
    margin-top: 0.75em;
    font-size: 0.875em;
    line-height: 1.5;
}

/* Responsividade */
@media (max-width: 768px) {
    .alert-message {
        padding: 0.875em 1em;
        gap: 0.75em;
    }

    .alert-message_icon {
        min-width: 2em;
        height: 2em;
        font-size: 0.875em;
    }

    .alert-message_title,
    .alert-message_description {
        font-size: 0.8125em;
    }
}
</style>